/**
 * One operator action log for the layer-stack match page: ingest,
 * import, game-facts saves, corrections and every build (summary-card
 * Build next/all and the Pipeline panel) write here, so what just ran
 * — and what failed — is visible in one place above the layer panels.
 *
 * PER GAME, AND KEPT ON THE SERVER (migration 116): every line is also
 * sent to /api/v1/opsqa/games/<id>/action-log, so any platform admin
 * opening the game sees every operator's lines, and they are kept. The
 * log lived in this tab's sessionStorage only (Ben, 2026-10-19). The tab
 * still keeps its own copy: a line shows at once, and one the server did
 * not take stays visible, marked "not saved", instead of vanishing.
 *
 * Clear hides the lines so far in this tab only; the kept log is not
 * deleted.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState,
         type ReactNode } from 'react'

import { api } from '../lib/api'

export type ActionKind = 'start' | 'done' | 'fail' | 'info'
type Entry = {
  ts: number; kind: ActionKind; msg: string
  /** The server's id once it took the line; absent until then. */
  id?: number
  /** Who wrote it (the server's record), once saved or loaded. */
  user?: string | null
  /** The server refused it or could not be reached. */
  unsaved?: boolean
  /** Written in this tab: ts is this browser's clock, not the server's. */
  local?: boolean
}
type ActionLog = {
  entries: Entry[]
  log: (kind: ActionKind, msg: string) => void
  clear: () => void
  /** Bumped on every 'done' entry: the stack changed, so every view on
   *  the page (pipeline, layer panels, summary chips) refetches. */
  stackVersion: number
}

const noop: ActionLog = {
  entries: [], log: () => {}, clear: () => {}, stackVersion: 0,
}
const ActionLogContext = createContext<ActionLog>(noop)

const MAX_ENTRIES = 500

function storageKey(gameId: number) {
  return `layerstack.actionlog.${gameId}`
}

function readLog(gameId: number | undefined): Entry[] {
  if (gameId == null) return []
  try {
    const v = JSON.parse(sessionStorage.getItem(storageKey(gameId)) ?? '[]')
    return Array.isArray(v) ? (v as Entry[]) : []
  } catch { return [] }
}

function writeLog(gameId: number, entries: Entry[]) {
  try {
    sessionStorage.setItem(storageKey(gameId),
                           JSON.stringify(entries.slice(-MAX_ENTRIES)))
  } catch { /* quota or private mode: the log stays in memory */ }
}

function clearedKey(gameId: number) {
  return `layerstack.actionlog.${gameId}.cleared`
}

/** What Clear hid in this tab: lines the server had up to `id`, and this
 *  tab's own lines up to `ts`. Each is compared on its own clock: a
 *  loaded line's ts is the server's, and a browser clock ahead of it hid
 *  lines written after Clear once they were reloaded (SDET, PR #796). */
type Cleared = { id: number; ts: number }

function readCleared(gameId: number | undefined): Cleared | null {
  if (gameId == null) return null
  try {
    const v = JSON.parse(sessionStorage.getItem(clearedKey(gameId)) ?? 'null') as Cleared | null
    return v && typeof v.id === 'number' && typeof v.ts === 'number' ? v : null
  } catch { return null }
}

function hiddenBy(c: Cleared, e: Entry): boolean {
  return e.local ? e.ts <= c.ts : (e.id ?? 0) <= c.id
}

/** The server's cap on a line (ops_qa_api.ACTION_LOG_MAX_CHARS): the tab
 *  keeps the whole text, the server the first this many characters. */
const MAX_CHARS = 2000

/** This tab's copy with one line changed, matched by when and what. */
function patchStored(gameId: number, entry: Entry, patch: Partial<Entry>) {
  writeLog(gameId, readLog(gameId).map((e) => (
    e.ts === entry.ts && e.msg === entry.msg && e.id === undefined
      ? { ...e, ...patch } : e)))
}

type ServerEntry = { id: number; ts: number; kind: ActionKind; msg: string
                     user: string | null }

/** How far apart a tab's line and the server's copy of it may be in time:
 *  the browser's clock, then the request, then the server's. */
const SAME_LINE_MS = 5 * 60 * 1000

/** The kept log, with this tab's lines the server does not have (not yet
 *  saved, or refused) — by time. A tab line with no id whose reply was
 *  lost may still be on the server: one server line of the same kind and
 *  text, close in time and not already claimed, is that line (SDET,
 *  PR #796) — it would otherwise show twice after a reload. */
function merge(server: ServerEntry[], local: Entry[]): Entry[] {
  const ids = new Set(server.map((e) => e.id))
  // a server line this tab already holds by id is no other line's twin
  const claimed = new Set<number>(
    local.flatMap((e) => (e.id !== undefined && ids.has(e.id) ? [e.id] : [])))
  const extra = local.filter((e) => {
    if (e.id !== undefined) return !ids.has(e.id)
    const twin = server.find((s) => !claimed.has(s.id) && s.kind === e.kind
      && s.msg === e.msg.slice(0, MAX_CHARS) && Math.abs(s.ts - e.ts) <= SAME_LINE_MS)
    if (twin) { claimed.add(twin.id); return false }
    return true
  })
  return [...server, ...extra].sort((a, b) => a.ts - b.ts).slice(-MAX_ENTRIES)
}

/** gameId: whose log this is. Omitted (tests, standalone use): in-memory
 *  only. Remount per game (key={gameId}) so state never crosses games.
 *
 *  An entry is written to its game's STORED log at once, not only to this
 *  provider's state: a reply that lands after the operator switched games
 *  (a "Queued …", a refused build) comes back to a provider that is gone,
 *  and was lost (2026-09-30). It still goes only to the game that sent
 *  it, and shows when that game is opened again. */
export function ActionLogProvider({ gameId, children }: {
  gameId?: number; children: ReactNode
}) {
  const [entries, setEntries] = useState<Entry[]>(() => readLog(gameId))
  const [cleared, setCleared] = useState(() => readCleared(gameId))
  const [stackVersion, setStackVersion] = useState(0)
  useEffect(() => {
    if (gameId == null) return
    let alive = true
    api<{ entries: ServerEntry[] }>(`/api/v1/opsqa/games/${gameId}/action-log`)
      .then((d) => { if (alive) setEntries(merge(d.entries, readLog(gameId))) })
      .catch(() => { /* this tab's copy still shows */ })
    return () => { alive = false }
  }, [gameId])
  const log = useCallback((kind: ActionKind, msg: string) => {
    const entry: Entry = { ts: Date.now(), kind, msg, local: true }
    // read-append-write: another writer (a late reply) may have added one
    if (gameId != null) writeLog(gameId, [...readLog(gameId), entry])
    setEntries((prev) => [...prev, entry].slice(-MAX_ENTRIES))
    if (kind === 'done') setStackVersion((v) => v + 1)
    if (gameId == null) return
    // Sent whatever page is showing by then: a reply landing after the
    // operator switched games still reaches its own game's kept log.
    const settle = (patch: Partial<Entry>) => {
      patchStored(gameId, entry, patch)
      // matched by when and what: a load from the server may have
      // replaced the object since
      setEntries((prev) => prev.map((e) => (
        e.ts === entry.ts && e.msg === entry.msg && e.id === undefined
          ? { ...e, ...patch } : e)))
    }
    api<{ id: number; user: string | null }>(
      `/api/v1/opsqa/games/${gameId}/action-log`,
      { method: 'POST', body: JSON.stringify({ kind, msg }) })
      .then((r) => settle({ id: r.id, user: r.user }))
      .catch(() => settle({ unsaved: true }))
  }, [gameId])
  const clear = useCallback(() => {
    const c = { id: Math.max(0, ...entries.map((e) => e.id ?? 0)), ts: Date.now() }
    if (gameId != null) {
      try { sessionStorage.setItem(clearedKey(gameId), JSON.stringify(c)) } catch { /* quota */ }
    }
    setCleared(c)
  }, [gameId, entries])
  const shown = cleared ? entries.filter((e) => !hiddenBy(cleared, e)) : entries
  return (
    <ActionLogContext.Provider value={{ entries: shown, log, clear, stackVersion }}>
      {children}
    </ActionLogContext.Provider>
  )
}

/** Outside a provider this is a no-op, so components stay usable alone. */
export function useActionLog(): ActionLog {
  return useContext(ActionLogContext)
}

/** When a line was logged: the DATE as well as the time — the log is
 *  stored per game and read back days later (Ben, 2026-10-22). */
export function logStamp(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

const COLORS: Record<ActionKind, string> = {
  start: '#eab308', done: '#22c55e', fail: '#ef4444', info: '#8b8b8b',
}

// Open/collapsed is a per-browser preference, not per game.
const COLLAPSED_KEY = 'layerstack.actionlog.collapsed'

export function ActionLogPanel() {
  const { entries, clear } = useActionLog()
  const ref = useRef<HTMLPreElement>(null)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === '1' } catch { return false }
  })
  const toggle = () => setCollapsed((c) => {
    try { localStorage.setItem(COLLAPSED_KEY, c ? '0' : '1') } catch { /* quota */ }
    return !c
  })
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight
  }, [entries, collapsed])
  const last = entries[entries.length - 1]
  return (
    <section style={{ marginBottom: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', marginBottom: '4px' }}>
        <button onClick={toggle} aria-expanded={!collapsed}
                className="t-label"
                style={{ fontSize: '11px', background: 'none', border: 0,
                         padding: 0, cursor: 'pointer', color: 'inherit' }}>
          {collapsed ? '\u25b8' : '\u25be'} Action log
          {collapsed && entries.length > 0 && ` (${entries.length})`}
        </button>
        {collapsed && last && (
          <span style={{ fontSize: '11px', color: COLORS[last.kind],
                         flex: 1, marginLeft: '8px', overflow: 'hidden',
                         textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {last.msg}
          </span>
        )}
        {!collapsed && entries.length > 0 && (
          <button className="adm-btn" style={{ fontSize: '9px', padding: '1px 6px' }}
                  title="Hides these lines in this tab. The log is kept for every admin."
                  onClick={clear}>
            Clear
          </button>
        )}
      </div>
      {!collapsed && (
        <pre ref={ref}
             style={{ fontSize: '11px', height: '19.5em', overflow: 'auto',
                      background: '#1a1a1e', color: '#d4d4d4', padding: '6px 8px',
                      borderRadius: '4px', margin: 0, whiteSpace: 'pre-wrap',
                      lineHeight: '1.5' }}>
          {entries.length === 0
            ? <span style={{ color: '#666' }}>No actions yet.</span>
            : entries.map((e, i) => (
              <span key={i}>
                <span style={{ color: '#666' }}>{logStamp(e.ts)}</span>
                {e.user && <span style={{ color: '#666' }} title={e.user}>
                  {' '}{e.user.split('@')[0]}</span>}
                {' '}<span style={{ color: COLORS[e.kind] }}>{e.msg}</span>
                {e.unsaved && <span style={{ color: '#666' }}
                                    title="The server did not take this line: it is in this tab only">
                  {' '}(not saved)</span>}{'\n'}
              </span>
            ))}
        </pre>
      )}
    </section>
  )
}
